import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Check, Save } from "lucide-react";

const field = "mt-2 mb-1";

export default function Account() {
  const { user, updateAccount, logout } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newEmail, setNewEmail] = useState(user?.email || "");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [ok, setOk] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setOk(false);
    if (!currentPassword) return setError("Enter your current password to confirm changes.");
    if (newPassword && newPassword !== confirm) return setError("New passwords don't match.");
    const payload = { current_password: currentPassword };
    if (newEmail && newEmail !== user?.email) payload.new_email = newEmail;
    if (newPassword) payload.new_password = newPassword;
    if (!payload.new_email && !payload.new_password)
      return setError("Change your email and/or password, then save.");

    setSaving(true);
    const res = await updateAccount(payload);
    setSaving(false);
    if (res.ok) {
      setOk(true);
      setCurrentPassword("");
      setNewPassword("");
      setConfirm("");
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--cream)]">
      <header className="bg-[var(--charcoal)] text-[#F3E9DC]">
        <div className="max-w-[700px] mx-auto px-5 lg:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/assets/seraphina-logo-hero.png" alt="Seraphina" className="h-9 w-9 object-contain" />
            <div>
              <h1 className="font-display text-2xl leading-none">Account Settings</h1>
              <p className="text-[#c9bba9] text-xs mt-1">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-sm hover:text-[var(--gold-soft)] flex items-center gap-2"><ArrowLeft size={15} /> Dashboard</Link>
            <button onClick={logout} className="text-sm hover:text-[var(--gold-soft)]">Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-[700px] mx-auto px-5 lg:px-10 py-10">
        <form onSubmit={submit} data-testid="account-form" className="soft-card rounded-2xl p-8 bg-white">
          <h2 className="font-display text-3xl text-[var(--charcoal)]">Change Login Email &amp; Password</h2>
          <p className="text-sm text-[var(--charcoal-soft)] mt-2">
            Update the email and password you use to sign in. Changes save immediately — no redeploy needed.
          </p>

          <div className="mt-7">
            <label className="eyebrow">Login Email</label>
            <Input data-testid="account-email" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} className={field} placeholder="info@seraphinacollective.net" />
          </div>

          <div className="mt-5 grid sm:grid-cols-2 gap-5">
            <div>
              <label className="eyebrow">New Password <span className="normal-case tracking-normal text-[var(--charcoal-soft)]">(optional)</span></label>
              <Input data-testid="account-new-password" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className={field} placeholder="Leave blank to keep current" />
            </div>
            <div>
              <label className="eyebrow">Confirm New Password</label>
              <Input data-testid="account-confirm-password" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={field} placeholder="Repeat new password" />
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-[var(--line)]">
            <label className="eyebrow">Current Password <span className="normal-case tracking-normal text-[var(--charcoal-soft)]">(required to confirm)</span></label>
            <Input data-testid="account-current-password" type="password" value={currentPassword} onChange={(e) => { setCurrentPassword(e.target.value); setError(""); }} className={field} placeholder="Enter your current password" />
          </div>

          {error && <p data-testid="account-error" className="text-red-500 text-sm mt-4">{error}</p>}
          {ok && <p data-testid="account-success" className="text-green-600 text-sm mt-4 flex items-center gap-2"><Check size={16} /> Saved — use your new login next time.</p>}

          <div className="flex gap-3 mt-7">
            <button type="button" onClick={() => navigate("/admin")} className="btn-outline-gold rounded-md px-6 py-3">Cancel</button>
            <button type="submit" data-testid="account-save" disabled={saving} className="btn-gold rounded-md px-8 py-3 flex items-center gap-2 disabled:opacity-60">
              <Save size={16} /> {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
